/**
 * useToast Hook
 *
 * Custom hook for managing toast notifications.
 * Provides methods to show success, error, info, and warning toasts.
 *
 * @module hooks/useToast
 */

import { useState, useCallback } from 'react';
import type { ToastType } from '@/components/ui/Toast';

export interface ToastState {
  isOpen: boolean;
  message: string;
  type: ToastType;
}

export interface UseToastReturn {
  /** Current toast state */
  toast: ToastState;
  /** Show a success toast */
  showSuccess: (message: string) => void;
  /** Show an error toast */
  showError: (message: string) => void;
  /** Show an info toast */
  showInfo: (message: string) => void;
  /** Show a warning toast */
  showWarning: (message: string) => void;
  /** Close the current toast */
  closeToast: () => void;
}

/**
 * Hook for managing toast notifications
 */
export const useToast = (): UseToastReturn => {
  const [toast, setToast] = useState<ToastState>({
    isOpen: false,
    message: '',
    type: 'info',
  });

  const showSuccess = useCallback((message: string) => {
    setToast({
      isOpen: true,
      message,
      type: 'success',
    });
  }, []);

  const showError = useCallback((message: string) => {
    setToast({
      isOpen: true,
      message,
      type: 'error',
    });
  }, []);

  const showInfo = useCallback((message: string) => {
    setToast({
      isOpen: true,
      message,
      type: 'info',
    });
  }, []);

  const showWarning = useCallback((message: string) => {
    setToast({
      isOpen: true,
      message,
      type: 'warning',
    });
  }, []);

  const closeToast = useCallback(() => {
    setToast((prev) => ({
      ...prev,
      isOpen: false,
    }));
  }, []);

  return {
    toast,
    showSuccess,
    showError,
    showInfo,
    showWarning,
    closeToast,
  };
};

export default useToast;